import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AiPill } from '@/components/AiPill';
import { SubmitButton } from '@/components/SubmitButton';
import { formatPercent } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { allocationsApi } from '@/api/allocations';
import { aiApi, type CategorySuggestion } from '@/api/ai';
import type { Category } from '@/api/categories';
import { aiDisabledCopy } from '@/api/errorCopy';
import {
  aiRefusal,
  ErrorCodes,
  isAiWalletRefusal,
  isApiError,
  type AiRefusal,
  type AiWalletReason,
} from '@/api/errors';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { TOP_UP_TITLE_ID, useTopUpSwap } from '@/features/aiWallet/useTopUpSwap';
import type { EnvelopeRegisterLine } from '@/api/registers';

const HIGH = 0.8;

function resolveAllocationId(
  item: CategorySuggestion | undefined,
  lines: EnvelopeRegisterLine[],
): number | null {
  if (item === undefined) return null;
  if (item.allocationId !== null) return item.allocationId;
  return lines[item.lineIndex]?.id ?? null;
}

function rowFor(
  item: CategorySuggestion,
  lines: EnvelopeRegisterLine[],
): EnvelopeRegisterLine | undefined {
  return lines[item.lineIndex];
}

interface SuggestCategoriesDialogProps {
  virtualAccountId: number;
  lines: EnvelopeRegisterLine[];
  categories: Category[];
  onClose: () => void;
  onApplied: () => void;
  onNotify: (message: string) => void;
}

export function SuggestCategoriesDialog({
  virtualAccountId,
  lines,
  categories,
  onClose,
  onApplied,
  onNotify,
}: SuggestCategoriesDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<CategorySuggestion[]>([]);
  const [checked, setChecked] = useState<ReadonlySet<number>>(() => new Set());
  const [applying, setApplying] = useState(false);
  const { swap: topUpSwap, swapped: toppingUp, attachPanelHost, requestHostClose } = useTopUpSwap();
  const [walletRefusal, setWalletRefusal] = useState<
    (AiRefusal & { reason: AiWalletReason }) | null
  >(null);
  // Try again after buying AI credit asks once more; nothing reruns on its own (MM-D-131).
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void aiApi
      .categorySuggestions(
        virtualAccountId,
        lines.map((line) => ({
          allocationId: line.id,
          payee: line.payee,
          amountMinor: line.amountMinor,
          sign: Math.sign(line.amountMinor) || 1,
        })),
      )
      .then((items) => {
        if (cancelled) return;
        setSuggestions(items);
        setChecked(
          new Set(
            items.flatMap((item, index) => {
              const fraction = item.confidence <= 1 ? item.confidence : item.confidence / 100;
              return fraction >= HIGH ? [index] : [];
            }),
          ),
        );
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        const refusal = aiRefusal(err);
        if (isAiWalletRefusal(refusal)) setWalletRefusal(refusal);
        else if (refusal !== null) setError(aiDisabledCopy(t, refusal));
        else if (isApiError(err) && err.code === ErrorCodes.AI_BUDGET_EXCEEDED)
          setError(t('ai.capped'));
        else setError(t('imports.errorGeneric'));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, lines, t, virtualAccountId]);

  async function handleApply() {
    setApplying(true);
    let ok = 0;
    let fail = 0;
    for (const index of [...checked].sort((a, b) => a - b)) {
      const item = suggestions[index];
      const allocationId = resolveAllocationId(item, lines);
      const categoryId = item?.categoryId;
      if (allocationId === null || categoryId === null || categoryId === undefined) {
        fail += 1;
        continue;
      }
      try {
        await allocationsApi.patchCategory(allocationId, { categoryId, scope: 'LINE' });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setApplying(false);
    onNotify(fail === 0 ? t('ai.applied', { count: ok }) : t('ai.appliedFailed', { ok, fail }));
    onApplied();
    onClose();
  }

  function nameOf(categoryId: number | null): string {
    if (categoryId === null) return '—';
    return categories.find((c) => c.id === categoryId)?.name ?? String(categoryId);
  }

  return (
    <Dialog
      open
      // While the top-up shows, the backdrop and Esc are its Cancel (TMMAPP-0119 item 16).
      onClose={() => requestHostClose(onClose)}
      fullWidth
      maxWidth="sm"
      aria-labelledby={toppingUp ? TOP_UP_TITLE_ID : 'suggest-cat-title'}
    >
      {/* TOP UP swaps this body for the top-up in place (DLG-005, TMMAPP-0118 C11). */}
      <Box sx={{ display: toppingUp ? 'none' : 'contents' }}>
        <DialogTitle id="suggest-cat-title">{t('envelope.suggestCategories')}</DialogTitle>
        <DialogContent>
          {status === 'loading' && <Typography>{t('common.loading')}</Typography>}
          {status === 'error' && error !== null && <Alert severity="warning">{error}</Alert>}
          {status === 'error' && walletRefusal !== null && (
            <AiWalletPrompt
              refusal={walletRefusal}
              onCloseHost={onClose}
              swap={topUpSwap}
              onTryAgain={() => {
                setWalletRefusal(null);
                setError(null);
                setStatus('loading');
                setAttempt((n) => n + 1);
              }}
            />
          )}
          {status === 'ready' && (
            <Stack spacing={1}>
              {suggestions.map((item, index) => {
                const fraction = item.confidence <= 1 ? item.confidence : item.confidence / 100;
                const percent = formatPercent(fraction, locale);
                const row = rowFor(item, lines);
                const payee = row?.payee ?? '—';
                const category = nameOf(item.categoryId);
                return (
                  <Stack key={index} direction="row" sx={{ gap: 1, alignItems: 'flex-start' }}>
                    <Checkbox
                      checked={checked.has(index)}
                      onChange={(_e, value) => {
                        setChecked((prev) => {
                          const next = new Set(prev);
                          if (value) next.add(index);
                          else next.delete(index);
                          return next;
                        });
                      }}
                      slotProps={{ input: { 'aria-label': `${payee} · ${category}` } }}
                    />
                    <Stack sx={{ gap: 0.5, alignItems: 'flex-start' }}>
                      {item.source === 'AI' ? (
                        // TMM-B9: the pill carries the confidence in words, never a bare percentage.
                        <>
                          <Typography>
                            {payee} · {category}
                          </Typography>
                          <AiPill confidence={item.confidence} />
                        </>
                      ) : (
                        // A MEMORY suggestion (payee memory) carries no mark (TMMAPP-0119 item 26).
                        <Typography>
                          {payee} · {category} · {percent}
                        </Typography>
                      )}
                      {/* Generated in the person's language (TMMAPI-0157, item 26b): every locale. */}
                      {item.reason !== null && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('ai.suggestionLabel')} <span>{item.reason}</span>
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <SubmitButton
            loading={applying}
            disabled={status !== 'ready' || checked.size === 0}
            onClick={() => void handleApply()}
          >
            {t('envelope.applySelected')}
          </SubmitButton>
        </DialogActions>
      </Box>
      <Box ref={attachPanelHost} sx={{ display: 'contents' }} data-top-up-host="" />
    </Dialog>
  );
}
